'use client';

import { classes } from '@/uiPrimitives/base/classes.helpers';
import { stack } from '@/uiPrimitives/layout/stack';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { box } from '@/uiPrimitives/layout/box';
import { AutocompleteSuggestion } from './Autocomplete.types';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { chevronIcon } from '@/uiIcons/chevronIcon.css';
import { icon } from '@/uiPrimitives/media/icon';
import { DOPLink } from '@/uiComposites/interactive/DOPLink';
import { addTestSelector } from '@dop/shared/helpers/testSelector';
import { textBlock } from '@/uiPrimitives/typography/text';
import { capitalizeFirstLetter } from '@dop/shared/helpers/string';
import { focusWithZIndex } from '@/uiPrimitives/base/focus.css';

const AutocompleteSuggestionItem = ({
	suggestion: { matchedBy, title, type, href },
}: {
	suggestion: AutocompleteSuggestion;
}) => {
	return (
		<li {...addTestSelector('uiAutocompleteSuggestionItem')}>
			<DOPLink
				href={href}
				data-link-type={'transactionBlockSuggestionLink'}
				className={classes(
					shelf({
						alignItems: 'center',
						gridTemplateColumns: 'minmax(0, 1fr) auto',
					}),
					box({ padding: '1 | h3', backgroundColor: 'negative:hoverGray' }),
					focusWithZIndex,
				)}
			>
				<div className={classes(stack({ rowGap: '0 | p' }))}>
					<p
						className={classes(
							textBlock({
								textDecorationLine: 'none',
								color: 'positive:noHover',
							}),
						)}
					>
						{matchedBy}
					</p>
					{(type || title) && (
						<p
							className={classes(
								textBlock({
									capSize: '-1 | small',
									color: 'positive:noHover',
									textDecorationLine: 'none',
								}),
							)}
						>
							{[capitalizeFirstLetter(type), title].join(' ')}
						</p>
					)}
				</div>
				<span className={classes(occupy({ inlineSize: '3 | h1' }))}>
					<i
						className={classes(
							icon({
								icon: chevronIcon,
								blockSize: '0 | p',
								fill: 'positiveBlue',
							}),
						)}
					/>
				</span>
			</DOPLink>
		</li>
	);
};

export const AutocompleteSuggestions = ({
	suggestions,
}: {
	suggestions: Array<AutocompleteSuggestion>;
}) => {
	if (suggestions.length === 0) return null;

	return (
		<div
			className={classes(
				occupy({
					blockSize: '0px',
					inlineSize: 'auto',
					alignItems: 'start',
					justifyItems: 'start',
				}),
			)}
		>
			<ul
				className={classes(
					stack({}),
					box({
						backgroundColor: 'negative',
						inlineSize: '100%',
						borderStyle: 'solid',
						borderColor: 'positiveBlue',
						borderWidth: '1px',
					}),
				)}
			>
				{suggestions.map((suggestion, i) => (
					<AutocompleteSuggestionItem
						key={`${suggestion.matchedBy}-${i}`}
						suggestion={suggestion}
					/>
				))}
			</ul>
		</div>
	);
};
